// Derive layer — pure aggregations over estimate lists.
//
// Every function takes already-scoped arrays (the orchestrator owns the
// filter pipeline and the skip-one-dimension "emphasis" scopes) and returns
// plain data. No hooks, no rendering — so the signals engine and unit tests
// can call them directly.

import type { ApiEstimate, VehicleSection } from "@/lib/api/types"
import { vehicleDisplayName } from "@/lib/api/types"
import { isDeferred } from "@/lib/api/estimates"
import { readIntakeMeta } from "@/lib/api/public-consumer"
import { SURFACE_LABEL } from "@/lib/intake/intake-config"
import {
  INTAKE_SURFACES,
  type IntakeSurface,
} from "@/components/estimates/consumer-intake-estimate/intake-flow-types"
import {
  AGING_HOURS, CAT, CATEGORY_LABELS, CATEGORY_MARGINS, CHANNEL_SPARK, FOAM,
  HIGH_VALUE_FLOOR, INDUSTRY_CLOSE_RATE, MINT_PALE, RAMP, REFINISH_TYPES,
  SECTION_LABELS, SOURCES, TIERS,
  ageHours, categoryMix, categoryMixWithScale, componentLabel, fmt$, fmt$full,
  isOpen, matchDim, median, pct, sourceOf,
  type CategoryKey, type DimFilters, type SourceKey,
} from "./room-model"

/* ── Headline block (the KPI + hero figures) ── */

export function deriveHeadline(args: {
  filtered: ApiEstimate[]; previousWindow: ApiEstimate[]; inScope: ApiEstimate[]
  windowMs: number; buckets: number; allChannels: boolean
}) {
  const { filtered, previousWindow, inScope, windowMs, buckets } = args
  const identified = filtered.reduce((s, e) => s + (e.avg ?? 0), 0)
  const completedList = filtered.filter(e => e.status === "Completed")
  const converted = completedList.reduce((s, e) => s + (e.avg ?? 0), 0)
  const valued = filtered.filter(e => (e.avg ?? 0) > 0)
  const avgValue = valued.length ? identified / valued.length : 0
  const closeRate = filtered.length ? Math.round((completedList.length / filtered.length) * 100) : 0

  const open = inScope.filter(isOpen)
  const aging = open.filter(e => ageHours(e) > AGING_HOURS)
  const aging48 = open.filter(e => ageHours(e) > 48)
  const agingValue = aging.reduce((s, e) => s + (e.avg ?? 0), 0)

  const prevIdentified = previousWindow.reduce((s, e) => s + (e.avg ?? 0), 0)
  const prevValued = previousWindow.filter(e => (e.avg ?? 0) > 0)

  // Per-bucket series for the hero + tile sparklines
  const bucketMs = windowMs / buckets
  const now = Date.now()
  const bucketOf = (e: ApiEstimate) => {
    const idx = buckets - 1 - Math.floor((now - new Date(e.createdAt).getTime()) / bucketMs)
    return idx < 0 ? 0 : Math.min(idx, buckets - 1)
  }
  const zero = () => Array.from({ length: buckets }, () => 0)
  const heroStack = SOURCES.map(s => ({ key: s.key, values: zero() }))
  const countSpark = zero(); const valueSpark = zero(); const completedSpark = zero()
  filtered.forEach(e => {
    const b = bucketOf(e)
    countSpark[b]++
    valueSpark[b] += e.avg ?? 0
    if (e.status === "Completed") completedSpark[b]++
    heroStack.find(h => h.key === sourceOf(e))!.values[b] += e.avg ?? 0
  })
  const heroTotals = zero().map((_, i) => heroStack.reduce((s, h) => s + h.values[i], 0))

  const vehicleIds = new Set(filtered.map(e => e.vehicle?.id).filter(Boolean))
  const damagedVehicleIds = new Set(filtered.filter(e => (e.lineItems?.length ?? 0) > 0 || (e.avg ?? 0) > 0).map(e => e.vehicle?.id).filter(Boolean))

  // Conversion funnel — the REAL pipeline stages (each bar = at-or-beyond the
  // stage): Pending → Confirmed → In progress → Scheduled → Completed, with
  // Declined as a side count. Semantics per the CEO: Pending = nobody's acted,
  // Confirmed = body shop confirmed, In progress = reaching out.
  const atOrBeyond = (statuses: string[]) => filtered.filter(e => statuses.includes(e.status)).length
  const confirmedPlus = atOrBeyond(["Confirmed", "InProgress", "Scheduled", "Completed"])
  const inProgressPlus = atOrBeyond(["InProgress", "Scheduled", "Completed"])
  const scheduledPlus = atOrBeyond(["Scheduled", "Completed"])
  const declinedCount = atOrBeyond(["Declined"])
  const daysToSchedule = filtered
    .filter(e => e.scheduledAt)
    .map(e => (new Date(e.scheduledAt as string).getTime() - new Date(e.createdAt).getTime()) / 86400000)
    .filter(v => v >= 0)
  const daysToComplete = completedList
    .map(e => (new Date(e.updatedAt).getTime() - new Date(e.createdAt).getTime()) / 86400000)
    .filter(v => v >= 0)

  // High-value close rate — do the biggest jobs convert as well as the rest?
  const highValue = filtered.filter(e => (e.avg ?? 0) > HIGH_VALUE_FLOOR)
  const highValueClosed = highValue.filter(e => e.status === "Completed").length
  const highValueCloseRate = highValue.length ? Math.round((highValueClosed / highValue.length) * 100) : 0

  const industryYield = identified * (INDUSTRY_CLOSE_RATE / 100)
  const gap = industryYield - converted

  return {
    identified, converted,
    openCount: open.length, completedCount: completedList.length,
    avgValue, closeRate, agingCount: aging.length, aging48Count: aging48.length, agingValue,
    identifiedTrend: pct(identified, prevIdentified),
    avgValueTrend: pct(avgValue, prevValued.length ? prevIdentified / prevValued.length : 0),
    hasPrior: previousWindow.length > 0,
    heroStack, heroTotals, countSpark, valueSpark, completedSpark,
    vehiclesEvaluated: vehicleIds.size, vehiclesWithDamage: damagedVehicleIds.size,
    damageRate: vehicleIds.size ? Math.round((damagedVehicleIds.size / vehicleIds.size) * 100) : 0,
    highValueCount: highValue.length, highValueCloseRate,
    total: filtered.length,
    funnel: {
      found: filtered.length, confirmed: confirmedPlus, inProgress: inProgressPlus,
      scheduled: scheduledPlus, completed: completedList.length, declined: declinedCount,
      medToSchedule: median(daysToSchedule), medToComplete: median(daysToComplete),
    },
    gap,
  }
}
export type Headline = ReturnType<typeof deriveHeadline>

/* ── Channels ── */

// Card-tone (lowercase) surface label — "QR code" keeps its acronym.
const surfaceTone = (s: IntakeSurface) =>
  SURFACE_LABEL[s].toLowerCase().replace(/^qr\b/, "QR")

// Observed counts by intake surface for the Consumer intake channel card,
// e.g. "website 3 · QR code 2 · kiosk 1". Null when no consumer estimate in
// scope carries an intake block (the card then renders no sub-line).
export function deriveIntakeSurfaceLine(consumerEstimates: ApiEstimate[]): string | null {
  const counts = new Map<IntakeSurface, number>()
  consumerEstimates.forEach(e => {
    const surface = readIntakeMeta(e)?.surface
    if (surface) counts.set(surface, (counts.get(surface) ?? 0) + 1)
  })
  if (counts.size === 0) return null
  return INTAKE_SURFACES
    .filter(s => (counts.get(s) ?? 0) > 0)
    .map(s => `${surfaceTone(s)} ${counts.get(s)}`)
    .join(" · ")
}

export function deriveChannelCards(forChannels: ApiEstimate[]) {
  return SOURCES.map(s => {
    const list = forChannels.filter(e => sourceOf(e) === s.key)
    return {
      ...s,
      count: list.length,
      identified: list.reduce((sum, e) => sum + (e.avg ?? 0), 0),
      color: CHANNEL_SPARK[s.key],
      surfaceLine: s.key === "consumer_intake" ? deriveIntakeSurfaceLine(list) : null,
    }
  })
}
export function deriveChannelCounts(usable: ApiEstimate[]) {
  const m = new Map<SourceKey, number>()
  usable.forEach(e => m.set(sourceOf(e), (m.get(sourceOf(e)) ?? 0) + 1))
  return m
}

/* ── Recovery queue (backlog — ignores time range) ── */

export function deriveRecovery(inScope: ApiEstimate[]) {
  const list = inScope
    .filter(e => isOpen(e) && ageHours(e) > AGING_HOURS)
    .sort((a, b) => (b.avg ?? 0) - (a.avg ?? 0))
  return { list, total: list.reduce((s, e) => s + (e.avg ?? 0), 0) }
}

/* ── Advisor efficiency ── */

export type AdvisorSortKey = "submitted" | "converted" | "closeRate" | "declineRate" | "identified" | "medDays"
export function deriveAdvisors(forAdvisors: ApiEstimate[], sort: { key: AdvisorSortKey; desc: boolean }) {
  const m = new Map<string, { id: string; name: string; submitted: number; converted: number; declined: number; identified: number; closeDays: number[] }>()
  forAdvisors.forEach(e => {
    if (!e.createdBy) return
    const id = String(e.createdBy.id)
    const cur = m.get(id) ?? { id, name: `${e.createdBy.firstName} ${e.createdBy.lastName}`.trim(), submitted: 0, converted: 0, declined: 0, identified: 0, closeDays: [] }
    cur.submitted++
    cur.identified += e.avg ?? 0
    if (e.status === "Completed") {
      cur.converted++
      cur.closeDays.push((new Date(e.updatedAt).getTime() - new Date(e.createdAt).getTime()) / 86400000)
    }
    if (e.status === "Declined") cur.declined++
    m.set(id, cur)
  })
  const rows = Array.from(m.values()).map(r => ({
    ...r,
    closeRate: r.submitted ? Math.round((r.converted / r.submitted) * 100) : 0,
    declineRate: r.submitted ? Math.round((r.declined / r.submitted) * 100) : 0,
    medDays: median(r.closeDays),
  }))
  const dir = sort.desc ? -1 : 1
  rows.sort((a, b) => (a[sort.key] > b[sort.key] ? dir : a[sort.key] < b[sort.key] ? -dir : 0))
  return { rows, maxSubmitted: Math.max(...rows.map(r => r.submitted), 1) }
}
export type AdvisorRow = ReturnType<typeof deriveAdvisors>["rows"][number]

/* ── Adoption pulse (is the program being used?) ── */

export function deriveActivity(usable: ApiEstimate[]) {
  const now = Date.now()
  const startOfToday = new Date(); startOfToday.setHours(0, 0, 0, 0)
  const weekAgo = now - 7 * 86400000
  let today = 0
  let week = 0
  usable.forEach(e => {
    const t = new Date(e.createdAt).getTime()
    if (t >= startOfToday.getTime()) today++
    if (t >= weekAgo) week++
  })
  return { today, week }
}

/**
 * Advisors on the roster who haven't scanned in the window. `roster` is the
 * team's full service-advisor list (from the reporting endpoint — it includes
 * 0-scan advisors); `activeIds` are those who created an estimate in-window.
 */
export function deriveInactiveAdvisors(roster: { userId: number; name: string }[] | undefined, activeIds: Set<string>) {
  if (!roster) return { quiet: [] as { userId: number; name: string }[], rosterSize: 0 }
  const quiet = roster.filter(r => !activeIds.has(String(r.userId)))
  return { quiet, rosterSize: roster.length }
}

/* ── Conversion trends over time (line/area charts) ── */

export function deriveTrends(filtered: ApiEstimate[], previousWindow: ApiEstimate[], buckets: number, windowMs: number) {
  const now = Date.now()
  const bucketMs = windowMs / buckets
  const idxAt = (iso: string, windowEnd: number) => {
    const i = buckets - 1 - Math.floor((windowEnd - new Date(iso).getTime()) / bucketMs)
    return i < 0 ? 0 : Math.min(i, buckets - 1)
  }
  const rows = Array.from({ length: buckets }, () => ({ found: 0, converted: 0, identified: 0, convertedValue: 0, prevCaptured: 0 }))
  filtered.forEach(e => {
    const b = rows[idxAt(e.createdAt, now)]
    b.found++
    b.identified += e.avg ?? 0
    if (e.status === "Completed") { b.converted++; b.convertedValue += e.avg ?? 0 }
  })
  // Ghost comparison: the previous equal-length window, bucket-aligned — the
  // analyst's "this period vs last period" read, from displayed data only.
  previousWindow.forEach(e => {
    if (e.status !== "Completed") return
    rows[idxAt(e.createdAt, now - windowMs)].prevCaptured += e.avg ?? 0
  })
  const out = rows.map((r, i) => {
    const t = now - (buckets - 1 - i) * bucketMs
    const label = new Date(t).toLocaleDateString("en-US", { month: "short", day: "numeric" })
    return {
      label,
      closeRate: r.found ? Math.round((r.converted / r.found) * 100) : 0,
      captured: r.convertedValue,
      prevCaptured: r.prevCaptured,
      found: r.found,
    }
  })
  // Best observed point (for the annotation dot) — a superlative of shown data.
  let best: { label: string; captured: number } | null = null
  out.forEach(r => { if (r.captured > 0 && (!best || r.captured > best.captured)) best = { label: r.label, captured: r.captured } })
  const hasPrev = out.some(r => r.prevCaptured > 0)
  return { rows: out, best, hasPrev }
}

/* ── Identified vs converted bars ── */

export function deriveCapture(d: Headline) {
  return [
    { name: "Identified", value: d.identified, color: CAT.deep, stroke: undefined as string | undefined, tip: fmt$full(d.identified) },
    { name: "Converted to RO", value: d.converted, color: CAT.brand, stroke: undefined as string | undefined, tip: fmt$full(d.converted) },
  ]
}

/* ── Sales by category ── */

export function deriveCategories(filtered: ApiEstimate[]) {
  const sum: Record<CategoryKey, number> = { labor: 0, parts: 0, paint: 0, sublet: 0 }
  filtered.forEach(e => {
    const mix = categoryMix(e)
    ;(Object.keys(sum) as CategoryKey[]).forEach(k => { sum[k] += mix[k] })
  })
  const colors: Record<CategoryKey, string> = { labor: CAT.deep, parts: CAT.brand, paint: CAT.light, sublet: CAT.gray }
  const total = Object.values(sum).reduce((a, b) => a + b, 0)
  return {
    total,
    rows: (Object.keys(sum) as CategoryKey[]).map(k => ({
      key: k, name: CATEGORY_LABELS[k], value: Math.round(sum[k]), color: colors[k],
      margin: CATEGORY_MARGINS[k],
      tip: `${fmt$full(sum[k])} (${total ? Math.round((sum[k] / total) * 100) : 0}%) · ${fmt$full(sum[k] * CATEGORY_MARGINS[k] / 100)} profit`,
    })),
  }
}
export type Categories = ReturnType<typeof deriveCategories>

/* ── Profit by category (the profit lens) ── */

export function deriveProfit(categories: Categories) {
  const rows = categories.rows.map(r => {
    const profit = Math.round(r.value * r.margin / 100)
    return {
      key: r.key, name: r.name, value: profit, color: r.color, margin: r.margin,
      tip: `${fmt$full(profit)} profit (${r.margin}% margin on ${fmt$full(r.value)})`,
    }
  })
  const total = rows.reduce((a, b) => a + b.value, 0)
  const blendedMargin = categories.total > 0 ? total / categories.total : 0
  return { rows, total, blendedMargin }
}

/* ── Value distribution (tier bars — click to filter) ── */

export function deriveDistribution(forTiers: ApiEstimate[], tier: string) {
  const valued = forTiers.filter(e => (e.avg ?? 0) > 0)
  return TIERS.map((t, i) => {
    const inTier = valued.filter(e => (e.avg as number) >= t.min && (e.avg as number) < t.max)
    const count = inTier.length
    const closed = inTier.filter(e => e.status === "Completed").length
    const closeRate = count ? Math.round((closed / count) * 100) : 0
    return {
      name: t.label, value: count, color: t.color, tierIdx: i, closeRate,
      dim: tier !== "" && tier !== String(i),
      tip: `${count} estimate${count !== 1 ? "s" : ""} (${valued.length ? Math.round((count / valued.length) * 100) : 0}%) · closes at ${closeRate}% — click to filter`,
    }
  })
}

/* ── Damage sections (frequency bars — click to filter) ── */

export function deriveSections(forSections: ApiEstimate[], section: string) {
  const m = new Map<string, number>()
  forSections.forEach(e => e.lineItems?.forEach(li => m.set(li.section, (m.get(li.section) ?? 0) + 1)))
  return Array.from(m.entries())
    .map(([key, value]) => ({
      key, value, name: SECTION_LABELS[key as VehicleSection] ?? key,
      dim: section !== "" && section !== key,
      tip: `${value} line item${value !== 1 ? "s" : ""} — click to filter`,
    }))
    .sort((a, b) => b.value - a.value).slice(0, 8)
}

/* ── Labor hours by type ── */

export function deriveLaborHours(filtered: ApiEstimate[]) {
  const sum = { body: 0, refinish: 0, prep: 0, other: 0 }
  filtered.forEach(e => e.lineItems?.forEach(li => (li.operations ?? []).forEach(op => {
    if (op.workType === "body_repair") sum.body += op.laborHours
    else if (op.workType === "refinish" || op.workType === "refinish_adder") sum.refinish += op.laborHours
    else if (op.workType === "refinish_prep") sum.prep += op.laborHours
    else sum.other += op.laborHours
  })))
  const total = sum.body + sum.refinish + sum.prep + sum.other
  const row = (name: string, value: number, color: string) => ({
    name, value: +value.toFixed(1), color,
    tip: `${value.toFixed(1)} h (${total ? Math.round((value / total) * 100) : 0}%)`,
  })
  return {
    total,
    rows: [row("Body repair", sum.body, CAT.deep), row("Refinish", sum.refinish, CAT.brand), row("Prep", sum.prep, CAT.light), row("Other", sum.other, CAT.gray)],
  }
}

/* ── Vehicle mix (top models by identified $) ── */

export function deriveVehicleMix(filtered: ApiEstimate[]) {
  const m = new Map<string, { name: string; count: number; identified: number }>()
  filtered.forEach(e => {
    const name = [e.vehicle?.make, e.vehicle?.model].filter(Boolean).join(" ") || "Unknown"
    const cur = m.get(name) ?? { name, count: 0, identified: 0 }
    cur.count++; cur.identified += e.avg ?? 0
    m.set(name, cur)
  })
  const rows = Array.from(m.values()).sort((a, b) => b.identified - a.identified).slice(0, 6)
  return { rows, max: Math.max(...rows.map(v => v.identified), 1) }
}

/* ── Make insights (volume · ADAS · value) ── */

const cleanCalibration = (desc: string) => desc.replace(/\s*\(sublet\)\s*$/i, "").trim()

export function deriveMakeInsights(forMakes: ApiEstimate[], networkAvg: number, makeFilter: string) {
  const m = new Map<string, { make: string; count: number; identified: number; adasCount: number; calib: Set<string> }>()
  forMakes.forEach(e => {
    const make = e.vehicle?.make || "Unknown"
    const cur = m.get(make) ?? { make, count: 0, identified: 0, adasCount: 0, calib: new Set<string>() }
    cur.count++
    cur.identified += e.avg ?? 0
    let hasAdas = false
    e.lineItems?.forEach(li => (li.operations ?? []).forEach(op => {
      if (op.workType === "adas_diagnostic") { hasAdas = true; if (op.description) cur.calib.add(cleanCalibration(op.description)) }
    }))
    if (hasAdas) cur.adasCount++
    m.set(make, cur)
  })
  const rows = Array.from(m.values()).map(r => ({
    make: r.make, count: r.count, identified: r.identified,
    avg: r.count ? r.identified / r.count : 0,
    adasCount: r.adasCount, adasRate: r.count ? Math.round((r.adasCount / r.count) * 100) : 0,
    calibrationTypes: Array.from(r.calib),
    dim: makeFilter !== "" && makeFilter !== r.make,
  }))
  const totalCount = rows.reduce((s, r) => s + r.count, 0)
  const byCount = [...rows].sort((a, b) => b.count - a.count)
  const byAvg = [...rows].filter(r => r.count > 0).sort((a, b) => b.avg - a.avg)
  const byAdas = [...rows].sort((a, b) => b.adasRate - a.adasRate || b.adasCount - a.adasCount)
  const topVolume = byCount[0]
  const topAvg = byAvg[0]
  const top3 = byCount.slice(0, 3)
  const top3Share = totalCount ? Math.round((top3.reduce((s, r) => s + r.count, 0) / totalCount) * 100) : 0
  const vsTopVolume = topVolume && topVolume.avg > 0 && topAvg ? topAvg.avg / topVolume.avg : 0
  return {
    rows, byCount, byAvg, byAdas, networkAvg,
    pills: {
      topVolume, topAvg, makesSeen: rows.length, top3Share,
      top3Names: top3.map(r => r.make), totalCount, vsTopVolume,
    },
  }
}
export type MakeInsights = ReturnType<typeof deriveMakeInsights>

/* ── Parts breakdown (by section, by named component) ── */

const PARTS_RAMP = ["#0D6B51", "#1D9E75", "#2FB185", "#5DCAA5", "#84D6B8", "#9FE1CB", "#BDEBDA"]

export function derivePartsBreakdown(forSections: ApiEstimate[]) {
  const bySec = new Map<string, { value: number; lines: number }>()
  const byComp = new Map<string, { value: number; lines: number }>()
  let totalValue = 0
  let totalLines = 0
  let oemValue = 0
  let amValue = 0
  for (const e of forSections) {
    const { scale, hasOps } = categoryMixWithScale(e)
    if (!hasOps) continue
    for (const li of e.lineItems ?? []) {
      const isOem = (li.partType ?? "OEM").toUpperCase().startsWith("OEM")
      for (const op of li.operations ?? []) {
        if (op.workType === "sublet_other" || op.partPrice <= 0) continue
        const val = op.partPrice * scale
        totalValue += val
        totalLines += 1
        if (isOem) oemValue += val; else amValue += val
        const s = bySec.get(li.section) ?? { value: 0, lines: 0 }
        s.value += val; s.lines += 1; bySec.set(li.section, s)
        const c = byComp.get(li.component) ?? { value: 0, lines: 0 }
        c.value += val; c.lines += 1; byComp.set(li.component, c)
      }
    }
  }

  // Sections: sort desc, fold anything under 3% into a single "Other".
  const secRows = Array.from(bySec.entries())
    .map(([key, v]) => ({ key, name: SECTION_LABELS[key as VehicleSection] ?? key, value: v.value, lines: v.lines }))
    .sort((a, b) => b.value - a.value)
  const bigSecs = secRows.filter(r => totalValue > 0 && r.value / totalValue >= 0.03)
  const smallSecs = secRows.filter(r => !(totalValue > 0 && r.value / totalValue >= 0.03))
  const bySection = bigSecs.map((r, i) => ({
    ...r, color: PARTS_RAMP[i % PARTS_RAMP.length],
    tip: `${fmt$full(r.value)} (${totalValue ? Math.round((r.value / totalValue) * 100) : 0}%) · ${r.lines} part line${r.lines !== 1 ? "s" : ""}`,
  }))
  if (smallSecs.length) {
    const value = smallSecs.reduce((s, r) => s + r.value, 0)
    const lines = smallSecs.reduce((s, r) => s + r.lines, 0)
    bySection.push({
      key: "__other__", name: "Other", value, lines, color: CAT.gray,
      tip: `${fmt$full(value)} across ${smallSecs.length} sections · ${lines} part lines`,
    })
  }

  const byComponent = Array.from(byComp.entries())
    .map(([key, v]) => ({ key, name: componentLabel(key), value: v.value, lines: v.lines }))
    .sort((a, b) => b.value - a.value)

  return {
    totalValue, totalLines, bySection, byComponent,
    oemValue, amValue, oemPct: oemValue + amValue > 0 ? Math.round((oemValue / (oemValue + amValue)) * 100) : 0,
    otherNote: smallSecs.length ? `Other includes ${smallSecs.map(s => s.name).join(", ")}.` : "",
  }
}
export type PartsBreakdown = ReturnType<typeof derivePartsBreakdown>

/* ── Paint / refinish materials ── */

export function derivePaint(filtered: ApiEstimate[]) {
  const byMakeMap = new Map<string, { hours: number; count: number }>()
  const byPanelMap = new Map<string, number>()
  let total = 0
  for (const e of filtered) {
    const make = e.vehicle?.make || "Unknown"
    let estHours = 0
    for (const li of e.lineItems ?? []) {
      let liHours = 0
      for (const op of li.operations ?? []) if (REFINISH_TYPES.has(op.workType)) liHours += op.laborHours
      if (liHours > 0) byPanelMap.set(li.section, (byPanelMap.get(li.section) ?? 0) + liHours)
      estHours += liHours
    }
    total += estHours
    const cur = byMakeMap.get(make) ?? { hours: 0, count: 0 }
    cur.hours += estHours; cur.count++; byMakeMap.set(make, cur)
  }
  const byMake = Array.from(byMakeMap.entries())
    .map(([make, v]) => ({ make, avg: v.count ? v.hours / v.count : 0, hours: v.hours, count: v.count }))
    .filter(r => r.hours > 0)
    .sort((a, b) => b.avg - a.avg)
  const byPanel = Array.from(byPanelMap.entries())
    .map(([key, hours]) => ({ key, name: SECTION_LABELS[key as VehicleSection] ?? key, hours, pct: total ? (hours / total) * 100 : 0 }))
    .sort((a, b) => b.hours - a.hours)
  return { byMake, byPanel, total }
}

/* ── Stores (group view) ── */

export function deriveStores(forStores: ApiEstimate[], usable: ApiEstimate[], f: DimFilters, isGroupView: boolean) {
  if (!isGroupView) return { rows: [], max: 1 }
  const m = new Map<string, { id: string; name: string; identified: number; converted: number; count: number; aging: number; activeAdvisors: Set<string> }>()
  forStores.forEach(e => {
    const id = String(e.dealership?.id ?? "")
    if (!id) return
    const s = m.get(id) ?? { id, name: e.dealership?.name ?? "", identified: 0, converted: 0, count: 0, aging: 0, activeAdvisors: new Set<string>() }
    s.count++
    s.identified += e.avg ?? 0
    if (e.status === "Completed") s.converted += e.avg ?? 0
    m.set(id, s)
  })
  const weekAgo = Date.now() - 7 * 86400000
  usable.filter(e => matchDim(e, f, "store")).forEach(e => {
    const s = m.get(String(e.dealership?.id ?? ""))
    if (!s) return
    if (isOpen(e) && ageHours(e) > AGING_HOURS) s.aging++
    // Adoption per store: distinct advisors who wrote an estimate this week.
    if (e.createdBy && new Date(e.createdAt).getTime() >= weekAgo) s.activeAdvisors.add(String(e.createdBy.id))
  })
  // Ranked by total opportunity (identified $) — per the CEO's Jul 14 review.
  const rows = Array.from(m.values())
    .map(({ activeAdvisors, ...s }) => ({ ...s, activeAdvisors7d: activeAdvisors.size }))
    .sort((a, b) => b.identified - a.identified)
  return { rows, max: Math.max(...rows.map(s => s.identified), 1) }
}

/* ── When opportunity arrives (weekday × hour heatmap) ── */

export const HEAT_HOURS = Array.from({ length: 12 }, (_, i) => i + 7) // 7:00–18:00
export function deriveHeatmap(filtered: ApiEstimate[]) {
  const grid: number[][] = Array.from({ length: 7 }, () => Array.from({ length: HEAT_HOURS.length }, () => 0))
  filtered.forEach(e => {
    const dt = new Date(e.createdAt)
    const row = (dt.getDay() + 6) % 7 // Mon = 0
    const col = HEAT_HOURS.indexOf(dt.getHours())
    if (col >= 0) grid[row][col]++
  })
  const flat = grid.flat()
  const max = Math.max(...flat, 1)
  const nonzero = flat.filter(v => v > 0)
  return { grid, max, hours: HEAT_HOURS, med: median(nonzero) }
}
export const heatColor = (v: number, max: number) => {
  if (v === 0) return "#F1F0EB"
  const t = v / max
  if (t < 0.25) return "#DCEFE5"
  if (t < 0.5) return RAMP[0]
  if (t < 0.75) return RAMP[1]
  return RAMP[3]
}

/* ── Vehicles evaluated (walkaround-backed damage rate) ── */

export type Walkaround = {
  id: number
  vehicleId: number | null
  source: SourceKey
  dealershipId: string | null
  advisorId: string | null
  estimateId: number | null
  createdAt: string
}

/* ── Pipeline dollar flow — the CEO's "where the money sits" (usage/adoption
      visuals v2). Every $ of identified opportunity mapped to its CURRENT
      stage; vocabulary: In motion (Pending→In progress) · Won (Scheduled +
      Completed) · Lost (Declined). Computed over the room's window. ── */

export const FLOW_STAGES = [
  { key: "Pending", label: "Pending", color: "#EF9F27" },
  { key: "Confirmed", label: "Confirmed", color: "#9FE1CB" },
  { key: "InProgress", label: "In Progress", color: "#5DCAA5" },
  { key: "Scheduled", label: "Scheduled", color: "#1D9E75" },
  { key: "Completed", label: "Completed", color: "#0F6E56" },
  { key: "Declined", label: "Declined", color: "#E24B4A" },
] as const

export function deriveDollarFlow(filtered: ApiEstimate[]) {
  const buckets = new Map<string, { count: number; value: number }>()
  let total = 0
  filtered.forEach(e => {
    const key = e.status === "Loading" ? "Pending" : e.status
    if (key === "Draft") return
    const b = buckets.get(key) ?? { count: 0, value: 0 }
    b.count++; b.value += e.avg ?? 0
    buckets.set(key, b)
    total += e.avg ?? 0
  })
  const stages = FLOW_STAGES.map(s => {
    const b = buckets.get(s.key) ?? { count: 0, value: 0 }
    return { ...s, count: b.count, value: b.value, share: total > 0 ? (b.value / total) * 100 : 0 }
  })
  const val = (k: string) => buckets.get(k)?.value ?? 0
  // Follow-up SLA: how fast does the shop confirm? Median created→updated of
  // currently-Confirmed estimates (updatedAt proxy — exact history needs BE).
  const confirmDays = filtered
    .filter(e => e.status === "Confirmed")
    .map(e => (new Date(e.updatedAt).getTime() - new Date(e.createdAt).getTime()) / 86400000)
    .filter(v => v >= 0)
  // The clock on parked money: how long has the oldest Confirmed sat there?
  const now = Date.now()
  const confirmedAges = filtered
    .filter(e => e.status === "Confirmed")
    .map(e => (now - new Date(e.updatedAt).getTime()) / 86400000)
  return {
    stages, total,
    inMotion: val("Pending") + val("Confirmed") + val("InProgress"),
    won: val("Scheduled") + val("Completed"),
    lost: val("Declined"),
    medianToConfirm: confirmDays.length >= 3 ? median(confirmDays) : null,
    confirmedCount: buckets.get("Confirmed")?.count ?? 0,
    confirmedValue: val("Confirmed"),
    oldestConfirmedDays: confirmedAges.length ? Math.max(...confirmedAges) : null,
  }
}
export type DollarFlow = ReturnType<typeof deriveDollarFlow>

/* ── Follow-up flags (backlog semantics — never hidden by the range) ── */

export function deriveFollowupFlags(inScope: ApiEstimate[]) {
  const now = Date.now()
  const hoursSince = (iso: string) => (now - new Date(iso).getTime()) / 3600000
  const active = (e: ApiEstimate) => !["Completed", "Declined", "Draft", "Loading"].includes(e.status)
  return {
    // Stuck-in-stage (time-in-current-stage via updatedAt — exact stage history needs BE support)
    pendingStale: inScope.filter(e => e.status === "Pending" && ageHours(e) > 24).length,
    confirmedStale: inScope.filter(e => e.status === "Confirmed" && hoursSince(e.updatedAt) > 48).length,
    missedAppointments: inScope.filter(e => e.scheduledAt && new Date(e.scheduledAt).getTime() < now && active(e)).length,
    upcomingAppointments: inScope.filter(e => {
      if (!e.scheduledAt || !active(e)) return false
      const t = new Date(e.scheduledAt).getTime()
      return t >= now && t <= now + 7 * 86400000
    }).length,
    parked: inScope.filter(e => active(e) && isDeferred(e)).length,
    // Follow-up blocker: you can't call a customer whose number was never captured.
    noContact: inScope.filter(e => active(e) && !e.vehicle?.customer?.phone && !e.vehicle?.customer?.email).length,
    // The comeback book: deferred estimates with a future appointment on the
    // books — scheduled returns, not losses. (Gated in the UI when thin.)
    comeback: (() => {
      const list = inScope.filter(e => active(e) && isDeferred(e) && e.scheduledAt && new Date(e.scheduledAt).getTime() > now)
      return { count: list.length, value: list.reduce((s, e) => s + (e.avg ?? 0), 0) }
    })(),
  }
}
export type FollowupFlags = ReturnType<typeof deriveFollowupFlags>

/* ── Usage & adoption section (the CEO's usage visuals v2) ─────────────────
   Period tabs (Today / WTD / MTD / All) drive: active-users dot matrix, lane
   capture (needs the dealer-provided lane-RO volume), estimates + spark,
   per-active-user vs target bullet, and the user-activity leaderboard. ── */

export type UsagePeriodKey = "today" | "wtd" | "mtd" | "all"

const businessDaysBetween = (from: Date, to: Date) => {
  let n = 0
  const d = new Date(from)
  d.setHours(0, 0, 0, 0)
  while (d.getTime() <= to.getTime()) {
    const dow = d.getDay()
    if (dow !== 0 && dow !== 6) n++
    d.setDate(d.getDate() + 1)
  }
  return Math.max(n, 1)
}

export function deriveUsageSection(args: {
  usable: ApiEstimate[]
  walkarounds: Walkaround[] | null
  roster: { userId: number; name: string }[] | undefined
  goals: { weeklyTarget?: number | null; monthlyTarget?: number | null; avgDailyLaneROs?: number | null }
  period: UsagePeriodKey
}) {
  const { usable, walkarounds, roster, goals, period } = args
  const now = new Date()
  const startToday = new Date(now); startToday.setHours(0, 0, 0, 0)
  const startWeek = new Date(startToday); startWeek.setDate(startWeek.getDate() - ((startWeek.getDay() + 6) % 7))
  const startMonth = new Date(now.getFullYear(), now.getMonth(), 1)
  const allDates = usable.map(e => new Date(e.createdAt).getTime())
  const programStart = new Date(allDates.length ? Math.min(...allDates) : startMonth.getTime())

  const periodStart: Date =
    period === "today" ? startToday : period === "wtd" ? startWeek : period === "mtd" ? startMonth : programStart
  const inPeriod = (iso: string) => new Date(iso).getTime() >= periodStart.getTime()

  const est = usable.filter(e => inPeriod(e.createdAt))
  const walks = walkarounds?.filter(w => inPeriod(w.createdAt)) ?? null

  /* Per-user rollup (roster first, so never/idle users stay visible) */
  type URow = { id: string; name: string; veh: number; est: number; value: number; lastActive: number | null }
  const m = new Map<string, URow>()
  for (const r of roster ?? []) m.set(String(r.userId), { id: String(r.userId), name: r.name, veh: 0, est: 0, value: 0, lastActive: null })
  // lastActive from ALL activity (not period-scoped) — idle detection is absolute.
  usable.forEach(e => {
    if (!e.createdBy) return
    const id = String(e.createdBy.id)
    const row = m.get(id) ?? { id, name: `${e.createdBy.firstName} ${e.createdBy.lastName}`.trim(), veh: 0, est: 0, value: 0, lastActive: null }
    const t = new Date(e.createdAt).getTime()
    row.lastActive = Math.max(row.lastActive ?? 0, t)
    if (inPeriod(e.createdAt)) { row.est++; row.value += e.avg ?? 0 }
    m.set(id, row)
  })
  if (walks) {
    // Vehicles column = walkaround evaluations per user (falls back to estimates when absent).
    walkarounds!.forEach(w => {
      if (!w.advisorId) return
      const row = m.get(w.advisorId)
      if (!row) return
      const t = new Date(w.createdAt).getTime()
      row.lastActive = Math.max(row.lastActive ?? 0, t)
      if (inPeriod(w.createdAt)) row.veh++
    })
  } else {
    m.forEach(r => { r.veh = r.est })
  }

  const threeBizDaysAgo = (() => { const d = new Date(startToday); let left = 3; while (left > 0) { d.setDate(d.getDate() - 1); const dow = d.getDay(); if (dow !== 0 && dow !== 6) left-- } return d.getTime() })()
  const users = Array.from(m.values())
    .map(r => ({
      ...r,
      // Sam's legend: Active = activity in period · Idle = nothing in 3+ business days · Never = enrolled, never active
      status: (r.veh > 0 || r.est > 0 ? "active" : r.lastActive == null ? "never" : r.lastActive < threeBizDaysAgo ? "idle" : "quiet") as "active" | "idle" | "never" | "quiet",
    }))
    .sort((a, b) => b.value - a.value || b.est - a.est || a.name.localeCompare(b.name))
  const activeCount = users.filter(u => u.status === "active").length

  /* Lane capture — vehicles evaluated vs lane RO volume (dealer-provided setting) */
  const evaluated = walks ? walks.length : est.length
  const bizDays = businessDaysBetween(periodStart, now)
  const laneROs = goals.avgDailyLaneROs ? Math.round(goals.avgDailyLaneROs * bizDays) : null
  const capturePct = laneROs && laneROs > 0 ? Math.min(Math.round((evaluated / laneROs) * 100), 100) : null

  /* Estimates + spark (bucket shape follows the period) */
  const identified = est.reduce((s, e) => s + (e.avg ?? 0), 0)
  let spark: { label: string; v: number }[]
  if (period === "today") {
    spark = Array.from({ length: 12 }, (_, i) => ({ label: `${(i + 7) > 12 ? i + 7 - 12 : i + 7}${i + 7 >= 12 ? "p" : "a"}`, v: 0 }))
    est.forEach(e => { const h = new Date(e.createdAt).getHours() - 7; if (h >= 0 && h < 12) spark[h].v++ })
  } else if (period === "wtd") {
    const days = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"]
    const nDays = Math.min(Math.floor((now.getTime() - startWeek.getTime()) / 86400000) + 1, 7)
    spark = Array.from({ length: nDays }, (_, i) => ({ label: days[i], v: 0 }))
    est.forEach(e => { const i = Math.floor((new Date(e.createdAt).getTime() - startWeek.getTime()) / 86400000); if (i >= 0 && i < nDays) spark[i].v++ })
  } else {
    const weeks = Math.max(Math.ceil((now.getTime() - periodStart.getTime()) / (7 * 86400000)), 1)
    const n = Math.min(weeks, 10)
    const span = (now.getTime() - periodStart.getTime()) / n || 1
    spark = Array.from({ length: n }, (_, i) => {
      const t = new Date(periodStart.getTime() + i * span)
      return { label: t.toLocaleDateString("en-US", { month: "short", day: "numeric" }), v: 0 }
    })
    est.forEach(e => { const i = Math.min(Math.floor((new Date(e.createdAt).getTime() - periodStart.getTime()) / span), n - 1); if (i >= 0) spark[i].v++ })
  }

  /* Per-active-user vs target (targets prorated from the goals config) */
  const weekly = goals.weeklyTarget ?? null
  const monthly = goals.monthlyTarget ?? null
  const target =
    period === "today" ? (weekly ? weekly / 5 : null)
      : period === "wtd" ? (weekly ? (weekly / 5) * Math.min(bizDays, 5) : null)
        : period === "mtd" ? (monthly ? (monthly / 22) * Math.min(bizDays, 22) : null)
          : (monthly ? (monthly / 22) * bizDays : null)
  const perUser = activeCount > 0 ? est.length / activeCount : 0

  // Accountability math, Sam-style: if everyone identified like the top user
  // this period, how much more would the store have found?
  const top = users[0]
  const uplift = top && top.value > 0
    ? users.slice(1).reduce((s, u) => s + Math.max(top.value - u.value, 0), 0)
    : 0

  // Best day of the period (skip for "today" — it IS one day).
  let bestDay: { label: string; count: number; value: number } | null = null
  if (period !== "today") {
    const byDay = new Map<string, { count: number; value: number; t: number }>()
    est.forEach(e => {
      const dt = new Date(e.createdAt); dt.setHours(0, 0, 0, 0)
      const k = dt.toISOString()
      const cur = byDay.get(k) ?? { count: 0, value: 0, t: dt.getTime() }
      cur.count++; cur.value += e.avg ?? 0
      byDay.set(k, cur)
    })
    for (const v of byDay.values()) {
      if (!bestDay || v.value > bestDay.value) bestDay = { label: new Date(v.t).toLocaleDateString("en-US", { month: "short", day: "numeric" }), count: v.count, value: v.value }
    }
    if (bestDay && bestDay.count < 2) bestDay = null
  }

  return {
    period, periodStart,
    users, activeCount, enrolled: users.length,
    evaluated, laneROs, capturePct,
    estimates: est.length, identified,
    spark,
    perUser, target,
    uplift, topName: top?.name ?? null, bestDay,
  }
}
export type UsageSection = ReturnType<typeof deriveUsageSection>

/* ── Program economics (Earn — leadership view) ── */

export function deriveEconomics(args: {
  usable: ApiEstimate[]
  payoutTotal: number | null
  totalLossLeadsPending: number | null
  totalLossLeadsAssigned: number | null
  walkarounds: Walkaround[] | null
}) {
  const { usable, payoutTotal, totalLossLeadsPending, totalLossLeadsAssigned, walkarounds } = args
  const now = new Date()
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).getTime()
  const d30 = now.getTime() - 30 * 86400000
  const completed = usable.filter(e => e.status === "Completed")
  const capturedMTD = completed.filter(e => new Date(e.updatedAt).getTime() >= monthStart).reduce((s, e) => s + (e.avg ?? 0), 0)
  const captured30 = completed.filter(e => new Date(e.updatedAt).getTime() >= d30).reduce((s, e) => s + (e.avg ?? 0), 0)
  const scans30 = walkarounds
    ? walkarounds.filter(w => new Date(w.createdAt).getTime() >= d30).length
    : usable.filter(e => new Date(e.createdAt).getTime() >= d30).length
  const capturedAll = completed.reduce((s, e) => s + (e.avg ?? 0), 0)

  // Month-over-month: this month vs the same window of last month (the owner's
  // "are we growing" question in one line).
  const prevMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1).getTime()
  const inMonth = (iso: string, from: number, to: number) => { const t = new Date(iso).getTime(); return t >= from && t < to }
  const foundThis = usable.filter(e => new Date(e.createdAt).getTime() >= monthStart).reduce((s, e) => s + (e.avg ?? 0), 0)
  const foundPrev = usable.filter(e => inMonth(e.createdAt, prevMonthStart, monthStart)).reduce((s, e) => s + (e.avg ?? 0), 0)
  const capturedPrev = completed.filter(e => inMonth(e.updatedAt, prevMonthStart, monthStart)).reduce((s, e) => s + (e.avg ?? 0), 0)
  const scansThis = walkarounds ? walkarounds.filter(w => new Date(w.createdAt).getTime() >= monthStart).length : usable.filter(e => new Date(e.createdAt).getTime() >= monthStart).length
  const scansPrev = walkarounds ? walkarounds.filter(w => inMonth(w.createdAt, prevMonthStart, monthStart)).length : usable.filter(e => inMonth(e.createdAt, prevMonthStart, monthStart)).length
  const mom = foundPrev > 0 || capturedPrev > 0 || scansPrev > 0
    ? { found: pct(foundThis, foundPrev), captured: pct(capturedMTD, capturedPrev), scans: pct(scansThis, scansPrev) }
    : null

  return {
    capturedMTD, mom,
    perScan: scans30 > 0 ? captured30 / scans30 : 0,
    scanBasis: walkarounds ? "walkaround" as const : "estimate" as const,
    incentiveTotal: payoutTotal,
    incentivePct: payoutTotal != null && capturedAll > 0 ? (payoutTotal / capturedAll) * 100 : null,
    totalLossFinds: usable.filter(e => e.totalLoss === true).length,
    // Name the finds — a stat becomes a story the GM retells.
    totalLossNames: usable.filter(e => e.totalLoss === true).slice(0, 2).map(e => vehicleDisplayName(e.vehicle)),
    totalLossLeadsPending, totalLossLeadsAssigned,
  }
}
export type Economics = ReturnType<typeof deriveEconomics>

/* ── Shop delivery extras (Deliver) ── */

export function deriveDelivery(inScope: ApiEstimate[], filtered: ApiEstimate[]) {
  // Booked, not-yet-delivered work: what the shop has committed to.
  let bookedHours = 0
  inScope.forEach(e => {
    if (e.status !== "Confirmed" && e.status !== "Scheduled") return
    e.lineItems?.forEach(li => (li.operations ?? []).forEach(op => { bookedHours += op.laborHours }))
  })
  // ADAS/calibration $ routed back to dealership service (windowed).
  let adasDollars = 0
  let lowConfidence = 0
  let afterHoursConsumer = 0
  filtered.forEach(e => {
    let low = false
    e.lineItems?.forEach(li => (li.operations ?? []).forEach(op => {
      if (op.workType === "adas_diagnostic") adasDollars += op.subletAmount ?? 0
      if (op.confidence === "low") low = true
    }))
    if (low) lowConfidence++
    if (sourceOf(e) === "consumer_intake") {
      const dt = new Date(e.createdAt)
      const day = dt.getDay(), hour = dt.getHours()
      if (day === 0 || day === 6 || hour < 7 || hour >= 18) afterHoursConsumer += e.avg ?? 0
    }
  })
  return { bookedHours, adasDollars, lowConfidence, afterHoursConsumer }
}
export type Delivery = ReturnType<typeof deriveDelivery>

/* ── Highlights — the analyst voice, inside the card it proves ─────────────
   Every highlight is an observed fact about DISPLAYED data (share, ratio,
   superlative with a count/date). Threshold-gated: below the bar → null and
   the card keeps its neutral sub. No fallbacks, no pattern claims ("Tuesdays
   are busy"), no imperatives — observational voice throughout. ── */

const MIN_SCOPE = 8 // estimates in scope before any claim is credible
const SHARE_BAR = 0.35
const RATIO_BAR = 1.5

export type HighlightAction =
  | { type: "tier"; value: string }
  | { type: "make"; value: string }
  | { type: "advisor"; value: string }
  | { type: "store"; value: string }
  | { type: "section"; value: string }
  | { type: "partsView" }
  | { type: "queue" }
export type HighlightFinding = { text: string; action?: HighlightAction }

export function deriveHighlights(inp: {
  total: number
  flow: DollarFlow
  distribution: ReturnType<typeof deriveDistribution>
  parts: PartsBreakdown
  makes: MakeInsights
  paint: ReturnType<typeof derivePaint>
  usage: UsageSection
  stores: ReturnType<typeof deriveStores>
}) {
  const { total, flow, distribution, parts, makes, paint, usage, stores } = inp
  const out: {
    flow?: HighlightFinding; distribution?: HighlightFinding; parts?: HighlightFinding
    makes?: HighlightFinding; paint?: HighlightFinding; leaderboard?: HighlightFinding
    stores?: HighlightFinding
  } = {}
  if (total < MIN_SCOPE) return out

  // Converting / flow — parked money with its clock.
  if (flow.confirmedCount >= 3 && flow.confirmedValue > 0 && flow.oldestConfirmedDays != null) {
    out.flow = {
      text: `${fmt$(flow.confirmedValue)} sits at Confirmed — the oldest for ${Math.round(flow.oldestConfirmedDays)} day${Math.round(flow.oldestConfirmedDays) !== 1 ? "s" : ""}`,
      action: { type: "queue" },
    }
  }

  // Work / distribution — where the money lives, and how it closes there.
  const tiersWithData = distribution.filter(t => t.value > 0)
  if (tiersWithData.length >= 2) {
    const modal = [...tiersWithData].sort((a, b) => b.value - a.value)[0]
    const highTiers = distribution.filter(t => t.tierIdx >= 3)
    const hvCount = highTiers.reduce((s, t) => s + t.value, 0)
    const hvClosed = highTiers.reduce((s, t) => s + Math.round((t.closeRate / 100) * t.value), 0)
    const hvRate = hvCount ? Math.round((hvClosed / hvCount) * 100) : 0
    if (modal.value >= 5 && modal.tierIdx < 3 && hvCount >= 4 && modal.closeRate > 0 && (modal.closeRate >= hvRate * RATIO_BAR || modal.closeRate - hvRate >= 12)) {
      out.distribution = {
        text: `${modal.value} of your jobs sit at ${modal.name} — they close at ${modal.closeRate}%, vs ${hvRate}% for $3k+`,
        action: { type: "tier", value: String(modal.tierIdx) },
      }
    } else if (modal.value / Math.max(tiersWithData.reduce((s, t) => s + t.value, 0), 1) >= SHARE_BAR) {
      out.distribution = {
        text: `${modal.value} of ${tiersWithData.reduce((s, t) => s + t.value, 0)} estimates land in the ${modal.name} tier`,
        action: { type: "tier", value: String(modal.tierIdx) },
      }
    }
  }

  // Parts — concentration on one named component.
  if (parts.totalValue > 0 && parts.byComponent.length >= 3) {
    const top = parts.byComponent[0]
    const share = top.value / parts.totalValue
    if (share >= 0.18) {
      out.parts = {
        text: `${top.name}s alone are ${Math.round(share * 100)}% of open parts demand (${top.lines} lines)`,
        action: { type: "partsView" },
      }
    }
  }

  // Makes — value intensity ratio between named makes (Sam's own "2.3× vs Toyota").
  if (makes.pills.topAvg && makes.pills.topVolume && makes.pills.topAvg.make !== makes.pills.topVolume.make
    && makes.pills.topAvg.count >= 3 && makes.pills.vsTopVolume >= RATIO_BAR) {
    out.makes = {
      text: `${makes.pills.topAvg.make} estimates average ${makes.pills.vsTopVolume.toFixed(1)}× ${makes.pills.topVolume.make}'s value`,
      action: { type: "make", value: makes.pills.topAvg.make },
    }
  }

  // Paint — booth-spend concentration by panel.
  if (paint.total > 0 && paint.byPanel.length >= 3 && paint.byPanel[0].pct >= 25) {
    out.paint = {
      text: `${paint.byPanel[0].name}s take ${Math.round(paint.byPanel[0].pct)}% of booth spend`,
      action: { type: "section", value: paint.byPanel[0].key },
    }
  }

  // People / leaderboard — top vs median, made visible.
  const valued = usage.users.filter(u => u.value > 0)
  if (valued.length >= 3) {
    const top = valued[0]
    const med = median(valued.map(u => u.value))
    if (med > 0 && top.value / med >= RATIO_BAR) {
      out.leaderboard = {
        text: `${top.name} found ${(top.value / med).toFixed(1)}× the median user this period (${fmt$(top.value)})`,
        action: { type: "advisor", value: top.id },
      }
    }
  }

  // Stores — network concentration.
  if (stores.rows.length >= 3) {
    const totalId = stores.rows.reduce((s, r) => s + r.identified, 0)
    const top = stores.rows[0]
    if (totalId > 0 && top.identified / totalId >= SHARE_BAR) {
      out.stores = {
        text: `${top.name} produces ${Math.round((top.identified / totalId) * 100)}% of network opportunity`,
        action: { type: "store", value: top.id },
      }
    }
  }

  return out
}
export type Highlights = ReturnType<typeof deriveHighlights>

export function deriveEvaluations(args: {
  inWindow: Walkaround[] | null
  prevWindow: Walkaround[] | null
  estimateCount: number
}) {
  const { inWindow, prevWindow, estimateCount } = args
  if (!inWindow || inWindow.length === 0) {
    return { hasData: false, evaluated: 0, withDamage: estimateCount, rate: 0, trend: 0, hasPrior: false }
  }
  const evaluated = inWindow.length
  const withDamage = inWindow.filter(w => w.estimateId != null).length || estimateCount
  const rate = evaluated ? Math.round((withDamage / evaluated) * 100) : 0
  const prevEvaluated = prevWindow?.length ?? 0
  return { hasData: true, evaluated, withDamage, rate, trend: pct(evaluated, prevEvaluated), hasPrior: prevEvaluated > 0 }
}
